import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Lock, RefreshCcw, Search, Undo2, Users, X } from 'lucide-react'
import Header from '@/components/layout/Header'
import Panel from '@/components/layout/Panel'
import { Button } from '@/components/ui/button'
import { useAuth, isOperator } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Dashboard → Activity.
 *
 * One question: who turns up when we play. Events used to share these axes and
 * this ranking, which made a movie night and a scrim move a player's number by
 * the same amount; the backend drops them now, so nothing on this page is a
 * blend of the two.
 *
 * Everything obeys the one range control at the top. The chart, the ranking and
 * the player panel read the same filtered list — a leaderboard counting 90 days
 * next to a chart drawing 30 is two truths on one screen.
 *
 * Colour: bone means "played", and it is the brightest thing in every mark.
 * Crimson is not a series — it stays what it is everywhere else in the panel,
 * the selection and the "this is you" mark. The ramp below is validated for
 * contrast and colour-vision separation against this surface.
 */

// ── Types ──────────────────────────────────────────────────────────────────────

type SignUpKind = 'primary' | 'bench' | 'tentative' | 'out' | ''

interface ActivitySignUp {
  user_id: string
  name: string
  class_name: string
  is_active: boolean
  kind: SignUpKind
  avatar_url: string | null
}

interface MatchItem {
  id: string
  title: string
  start_time: number
  map_name: string
  is_external: boolean
  sign_ups: ActivitySignUp[]
  active_count: number
}

interface ActivityResponse {
  items: MatchItem[]
  generated_at: number
  member_count: number
}

/** What one player did about one match. */
type PlayerState = 'played' | 'bench' | 'out' | 'absent'

// ── Palette ────────────────────────────────────────────────────────────────────

/**
 * An ordinal ramp, light → dark, on the panel's graphite ground. Not a
 * categorical set: the four states are degrees of the same thing (how much of
 * the match you were in), so they differ in lightness rather than hue.
 *
 * Checked with the dataviz validator against the dark surface — every adjacent
 * pair clears the colour-vision floor and every step clears 3:1 contrast.
 */
const INK = {
  played: '#efe0d8',   // bone — the club's type colour; the one that reads first
  bench:  '#9b9ba4',
  out:    '#6d6e78',
} as const

const STATE_FILL: Record<PlayerState, string> = {
  played: INK.played,
  bench:  INK.bench,
  out:    INK.out,
  absent: 'transparent',
}

const FALLBACK_AVATAR = 'https://cdn.discordapp.com/embed/avatars/0.png'

// ── Helpers ────────────────────────────────────────────────────────────────────

function playerState(m: MatchItem, uid: string): PlayerState {
  const su = m.sign_ups.find(s => s.user_id === uid)
  if (!su) return 'absent'
  if (su.is_active) return 'played'
  if (su.kind === 'bench') return 'bench'
  return 'out'
}

function pct(a: number, b: number) {
  return b === 0 ? 0 : Math.round((a / b) * 100)
}

/** Keep a fixed-position tooltip inside the window on both axes. */
function clampTip(x: number, y: number, w = 210, h = 150) {
  return {
    left: Math.min(Math.max(x - w / 2, 8), Math.max(window.innerWidth - w - 8, 8)),
    top:  Math.min(y + 16, Math.max(window.innerHeight - h - 8, 8)),
  }
}

// ── Chrome ─────────────────────────────────────────────────────────────────────

/** A hero number and what it counts. Data, so it is never uppercased. */
function Stat({ value, label, note, tone = 'ink' }: {
  value: string
  label: string
  note?: string
  tone?: 'ink' | 'primary'
}) {
  return (
    <div className="rounded-sm border border-white/10 bg-white/[0.035] px-3 py-2.5">
      <p className={`font-display text-xl font-bold leading-none tabular-nums ${
        tone === 'primary' ? 'text-primary' : 'text-foreground/90'
      }`}>
        {value}
      </p>
      <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60">
        {label}
      </p>
      {note && <p className="mt-1 text-[10px] leading-tight text-muted-foreground/50">{note}</p>}
    </div>
  )
}

function Chip({ active, onClick, title, children }: {
  active: boolean
  onClick: () => void
  title?: string
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`cursor-pointer rounded-sm border px-2.5 py-1 font-display text-[11px] font-semibold tracking-tight transition-colors ${
        active
          ? 'border-primary/60 bg-primary/15 text-foreground'
          : 'border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  )
}

function Avatar({ src, name, className }: { src: string | null; name: string; className: string }) {
  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-white/10 font-semibold text-muted-foreground ${className}`}>
        {name.slice(0, 1).toUpperCase()}
      </div>
    )
  }
  return (
    <img
      src={src}
      alt=""
      className={`object-cover ${className}`}
      onError={e => { (e.target as HTMLImageElement).src = FALLBACK_AVATAR }}
    />
  )
}

/**
 * Portalled to `body`. `Panel` is `backdrop-blur-sm`, and a backdrop filter
 * makes an element the containing block of its `position: fixed` children — so
 * inside the band the tip was placed relative to the band, not the window, and
 * landed far below the cursor with its lower half cut off.
 */
function Tip({ x, y, children }: { x: number; y: number; children: React.ReactNode }) {
  return createPortal(
    <div className="pointer-events-none fixed z-50" style={clampTip(x, y)}>
      <div className="w-[210px] rounded-sm border border-white/15 bg-black/90 px-3 py-2 shadow-xl backdrop-blur-sm">
        {children}
      </div>
    </div>,
    document.body,
  )
}

// ── TurnoutChart ───────────────────────────────────────────────────────────────

const PLOT_H   = 168   // px, baseline sits here
const PAD_TOP  = 16    // px, headroom for the one direct label
const Y_AXIS_W = 30    // px
const MARK_H   = 14    // px, the selected player's row under the axis
const LABEL_H  = 16    // px, month labels

/**
 * One bar per match, oldest on the left.
 *
 * Bars, not a line: matches are discrete and unevenly spaced, and a line
 * between two of them draws a turnout for evenings nobody played. The bar is
 * also the hit target, which is what makes the chart the way into a match.
 *
 * The two segments are nested (played ⊆ signed up), so they stack rather than
 * sit side by side, separated by a 2px gap of surface so neither edge is
 * guessed.
 */
function TurnoutChart({ matches, selectedId, onSelect, playerId, playerName, T, locale }: {
  matches: MatchItem[]              // chronological, oldest first
  selectedId: string | null
  onSelect: (id: string | null) => void
  playerId: string | null
  playerName: string | null
  T: ReturnType<typeof useLanguage>['T']
  locale: string
}) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [w, setW] = useState(0)
  const [hover, setHover] = useState<{ i: number; x: number; y: number } | null>(null)

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(e => setW(e[0].contentRect.width))
    ro.observe(el)
    setW(el.offsetWidth)
    return () => ro.disconnect()
  }, [])

  const n      = matches.length
  const svgW   = Math.max(w - Y_AXIS_W, 0)
  const slot   = n > 0 ? svgW / n : 0
  const barW   = Math.max(3, Math.min(22, slot - 4))
  const radius = Math.min(3, barW / 3)

  const rawMax  = Math.max(1, ...matches.map(m => m.sign_ups.length))
  const niceMax = Math.max(5, Math.ceil(rawMax / 5) * 5)

  const toY = (v: number) => PAD_TOP + (1 - v / niceMax) * (PLOT_H - PAD_TOP)
  const cx  = (i: number) => (i + 0.5) * slot

  const gridValues = [niceMax, Math.round(niceMax * 0.5), 0]

  // The one direct label: the best turnout in the range. A number on every bar
  // is a table pretending to be a chart.
  const bestIdx = matches.reduce(
    (best, m, i) => (m.active_count > (matches[best]?.active_count ?? -1) ? i : best), 0,
  )

  const monthMarks: { label: string; x: number }[] = []
  let lastMonth = -1
  matches.forEach((m, i) => {
    const d = new Date((m.start_time || 0) * 1000)
    if (d.getMonth() !== lastMonth) {
      lastMonth = d.getMonth()
      monthMarks.push({ label: d.toLocaleDateString(locale, { month: 'short' }), x: cx(i) })
    }
  })

  const showMarks = Boolean(playerId)
  const svgH = PLOT_H + (showMarks ? MARK_H : 0) + LABEL_H
  const hovered = hover ? matches[hover.i] : null

  const fmtDay = (ts: number) =>
    ts ? new Date(ts * 1000).toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : '-'

  return (
    <div className="space-y-3">
      <div ref={wrapRef} className="relative select-none" style={{ height: svgH }}>
        {/* Y axis — two values and the floor; a denser scale would out-ink the bars */}
        <div className="pointer-events-none absolute left-0 top-0" style={{ width: Y_AXIS_W, height: PLOT_H }}>
          {gridValues.map(v => (
            <span
              key={v}
              className="absolute right-0 pr-1.5 text-[9px] tabular-nums text-muted-foreground/40"
              style={{ top: toY(v) - 6 }}
            >
              {v}
            </span>
          ))}
        </div>

        {svgW > 0 && n > 0 && (
          <svg
            width={svgW}
            height={svgH}
            className="absolute"
            style={{ left: Y_AXIS_W, top: 0 }}
            onMouseLeave={() => setHover(null)}
          >
            {gridValues.map(v => (
              <line
                key={v}
                x1={0} x2={svgW} y1={toY(v)} y2={toY(v)}
                stroke="rgba(255,255,255,0.07)" strokeWidth={1}
              />
            ))}

            {matches.map((m, i) => {
              const total  = m.sign_ups.length
              const played = m.active_count
              const rest   = Math.max(0, total - played)
              const x      = cx(i) - barW / 2

              const yPlayed = toY(played)
              const hPlayed = Math.max(played > 0 ? 2 : 0, PLOT_H - yPlayed)
              const yRest   = toY(total)
              const hRest   = Math.max(rest > 0 ? 2 : 0, yPlayed - yRest - 2)

              const isSel  = selectedId === m.id
              const dimmed = selectedId !== null && !isSel
              const state  = playerId ? playerState(m, playerId) : null

              return (
                <g
                  key={m.id}
                  className="cursor-pointer"
                  aria-label={`${m.title} · ${fmtDay(m.start_time)} · ${played}/${total}`}
                  opacity={dimmed ? 0.55 : 1}
                  onMouseEnter={e => setHover({ i, x: e.clientX, y: e.clientY })}
                  onMouseMove={e => setHover({ i, x: e.clientX, y: e.clientY })}
                  onClick={() => onSelect(isSel ? null : m.id)}
                >
                  {/* Hit target: the whole column, so thin bars stay clickable */}
                  <rect x={cx(i) - slot / 2} y={0} width={slot} height={PLOT_H} fill="transparent" />

                  {hover?.i === i && !dimmed && (
                    <rect
                      x={cx(i) - slot / 2} y={0} width={slot} height={PLOT_H}
                      fill="rgba(255,255,255,0.05)"
                    />
                  )}

                  {hRest > 0 && (
                    <rect x={x} y={yRest} width={barW} height={hRest} rx={radius} fill={INK.out} />
                  )}
                  {hPlayed > 0 && (
                    <rect x={x} y={yPlayed} width={barW} height={hPlayed} rx={radius} fill={INK.played} />
                  )}

                  {/* Selection: a crimson foot under the bar, the panel's own
                      selection mark. Recolouring the bar would make the
                      selected match look like a different quantity. */}
                  {isSel && (
                    <rect
                      x={cx(i) - barW / 2 - 1} y={PLOT_H + 2}
                      width={barW + 2} height={2} rx={1}
                      fill="var(--primary)"
                    />
                  )}

                  {/* The selected player's own row, under the axis */}
                  {state && (
                    <rect
                      x={cx(i) - Math.min(barW, 10) / 2}
                      y={PLOT_H + (isSel ? 7 : 5)}
                      width={Math.min(barW, 10)} height={Math.min(barW, 10)}
                      rx={2}
                      fill={STATE_FILL[state]}
                      stroke={state === 'absent' ? 'rgba(255,255,255,0.18)' : 'none'}
                      strokeWidth={1}
                    />
                  )}

                  {i === bestIdx && played > 0 && (
                    <text
                      x={cx(i)} y={Math.min(yRest, yPlayed) - 6}
                      textAnchor="middle"
                      className="fill-foreground/70 text-[10px] font-semibold tabular-nums"
                    >
                      {played}
                    </text>
                  )}
                </g>
              )
            })}

            {monthMarks.map((mm, i) => (
              <text
                key={i}
                x={mm.x} y={svgH - 4}
                textAnchor="middle"
                className="fill-muted-foreground/45 text-[9px]"
              >
                {mm.label}
              </text>
            ))}
          </svg>
        )}

        {hovered && hover && (
          <Tip x={hover.x} y={hover.y}>
            <p className="truncate text-xs font-medium text-foreground/90">{hovered.title}</p>
            <p className="mt-0.5 text-[10px] text-muted-foreground">
              {fmtDay(hovered.start_time)}
              {hovered.map_name ? ` · ${hovered.map_name}` : ''}
            </p>
            <div className="mt-1.5 space-y-1 border-t border-white/10 pt-1.5">
              <TipRow color={INK.played} label={T.activity.seriesPlayed} value={hovered.active_count} />
              <TipRow
                color={INK.out}
                label={T.activity.seriesBenched}
                value={hovered.sign_ups.length - hovered.active_count}
              />
              <TipRow label={T.activity.totalSignUps} value={hovered.sign_ups.length} />
            </div>
            {playerId && playerName && (
              <p className="mt-1.5 border-t border-white/10 pt-1.5 text-[10px] text-muted-foreground">
                <span className="text-foreground/80">{playerName}</span>
                {' · '}
                {stateLabel(playerState(hovered, playerId), T)}
              </p>
            )}
          </Tip>
        )}
      </div>

      {/* Legend — two series, so it is always drawn */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" style={{ paddingLeft: Y_AXIS_W }}>
        <Swatch color={INK.played} label={T.activity.seriesPlayed} />
        <Swatch color={INK.out} label={T.activity.seriesBenched} />
        <span className="ml-auto text-[10px] text-muted-foreground/50">{T.activity.clickHint}</span>
      </div>
    </div>
  )
}

function TipRow({ color, label, value }: { color?: string; label: string; value: number }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
        <span
          className="h-2 w-2 shrink-0 rounded-[2px]"
          style={color ? { background: color } : { border: '1px solid rgba(255,255,255,0.25)' }}
        />
        {label}
      </span>
      <span className="text-[10px] font-semibold tabular-nums text-foreground/85">{value}</span>
    </div>
  )
}

function Swatch({ color, label, hollow = false }: { color?: string; label: string; hollow?: boolean }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
        style={hollow || !color
          ? { border: '1px solid rgba(255,255,255,0.22)' }
          : { background: color }}
      />
      <span className="text-[10px] text-muted-foreground/60">{label}</span>
    </span>
  )
}

function stateLabel(s: PlayerState, T: ReturnType<typeof useLanguage>['T']): string {
  return s === 'played' ? T.activity.statePlayed
    : s === 'bench'     ? T.activity.stateBench
    : s === 'out'       ? T.activity.stateOut
    : T.activity.stateAbsent
}

// ── MatchRoster ────────────────────────────────────────────────────────────────

/**
 * The selected match, opened under the chart. This is also the chart's table
 * view: every bar can be read as names rather than height.
 */
function MatchRoster({ match, onClose, onPickPlayer, T, locale }: {
  match: MatchItem
  onClose: () => void
  onPickPlayer: (id: string) => void
  T: ReturnType<typeof useLanguage>['T']
  locale: string
}) {
  const allGroups: { key: PlayerState; label: string; rows: ActivitySignUp[] }[] = [
    { key: 'played', label: T.activity.statePlayed, rows: match.sign_ups.filter(s => s.is_active) },
    { key: 'bench',  label: T.activity.stateBench,  rows: match.sign_ups.filter(s => !s.is_active && s.kind === 'bench') },
    { key: 'out',    label: T.activity.stateOut,    rows: match.sign_ups.filter(s => !s.is_active && s.kind !== 'bench') },
  ]
  const groups = allGroups.filter(g => g.rows.length > 0)

  return (
    <div className="mt-4 rounded-sm border border-primary/30 bg-white/[0.035] p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          {/* Match title is data — printed as written, never uppercased */}
          <h3 className="font-display text-sm font-bold leading-tight">{match.title}</h3>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {match.start_time
              ? new Date(match.start_time * 1000).toLocaleDateString(locale, {
                  day: 'numeric', month: 'long', year: 'numeric',
                })
              : '-'}
            {' · '}
            <span className={match.map_name ? '' : 'text-muted-foreground/50'}>
              {match.map_name || T.activity.mapUnknown}
            </span>
            {match.is_external && (
              <span className="ml-1.5 rounded-sm border border-white/15 bg-white/5 px-1 py-px text-[9px] uppercase tracking-wider">
                {T.activity.externalTag}
              </span>
            )}
          </p>
        </div>
        <p className="shrink-0 font-display text-lg font-bold leading-none tabular-nums">
          {match.active_count}
          <span className="text-sm text-muted-foreground/60">/{match.sign_ups.length}</span>
        </p>
        <button
          onClick={onClose}
          aria-label={T.activity.close}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground/50 transition-colors hover:bg-white/8 hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {groups.length === 0 ? (
        <p className="mt-3 rounded-sm border border-dashed border-white/10 py-5 text-center text-xs text-muted-foreground/70">
          {T.activity.noRoster}
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          {groups.map(g => (
            <div key={g.key}>
              <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/55">
                <span className="h-2 w-2 rounded-[2px]" style={{ background: STATE_FILL[g.key] }} />
                {g.label}
                <span className="tabular-nums text-muted-foreground/40">{g.rows.length}</span>
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {g.rows.map(s => (
                  <button
                    key={s.user_id}
                    onClick={() => onPickPlayer(s.user_id)}
                    title={s.class_name}
                    className="flex items-center gap-1.5 rounded-sm border border-white/10 bg-white/[0.04] py-1 pl-1 pr-2 transition-colors hover:border-white/20 hover:bg-white/[0.08]"
                  >
                    <Avatar src={s.avatar_url} name={s.name} className="h-5 w-5 shrink-0 rounded-full text-[9px]" />
                    <span className="max-w-[10rem] truncate text-[11px] text-foreground/85">{s.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── PlayerRecord ───────────────────────────────────────────────────────────────

/**
 * One cell per match, oldest on the left — not a calendar.
 *
 * A GitHub-style day grid was the wrong shape once events were gone: ninety
 * squares for a dozen matches is mostly a picture of the days we did not play.
 * Indexing by match instead makes every cell carry something, and lines the row
 * up one-for-one with the bars above it.
 */
function PlayerRecord({ matches, playerId, selectedId, onSelect, T, locale }: {
  matches: MatchItem[]
  playerId: string
  selectedId: string | null
  onSelect: (id: string | null) => void
  T: ReturnType<typeof useLanguage>['T']
  locale: string
}) {
  const [hover, setHover] = useState<{ m: MatchItem; x: number; y: number } | null>(null)

  return (
    <div>
      <div className="flex flex-wrap gap-[3px]">
        {matches.map(m => {
          const st    = playerState(m, playerId)
          const isSel = selectedId === m.id
          return (
            <button
              key={m.id}
              onClick={() => onSelect(isSel ? null : m.id)}
              onMouseEnter={e => setHover({ m, x: e.clientX, y: e.clientY })}
              onMouseLeave={() => setHover(null)}
              aria-label={`${m.title} · ${stateLabel(st, T)}`}
              className={`h-[15px] w-[15px] rounded-[3px] transition-transform hover:scale-125 ${
                isSel ? 'ring-1 ring-primary ring-offset-1 ring-offset-black/40' : ''
              }`}
              style={{
                background: STATE_FILL[st],
                border: st === 'absent' ? '1px solid rgba(255,255,255,0.14)' : undefined,
              }}
            />
          )
        })}
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-3.5 gap-y-1.5">
        <Swatch color={INK.played} label={T.activity.statePlayed} />
        <Swatch color={INK.bench} label={T.activity.stateBench} />
        <Swatch color={INK.out} label={T.activity.stateOut} />
        <Swatch hollow label={T.activity.stateAbsent} />
      </div>

      {hover && (
        <Tip x={hover.x} y={hover.y}>
          <p className="truncate text-xs font-medium text-foreground/90">{hover.m.title}</p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            {hover.m.start_time
              ? new Date(hover.m.start_time * 1000).toLocaleDateString(locale, { day: 'numeric', month: 'short' })
              : '-'}
          </p>
          <p className="mt-1.5 flex items-center gap-1.5 border-t border-white/10 pt-1.5 text-[10px] text-foreground/85">
            <span
              className="h-2 w-2 shrink-0 rounded-[2px]"
              style={{
                background: STATE_FILL[playerState(hover.m, playerId)],
                border: playerState(hover.m, playerId) === 'absent'
                  ? '1px solid rgba(255,255,255,0.25)' : undefined,
              }}
            />
            {stateLabel(playerState(hover.m, playerId), T)}
          </p>
        </Tip>
      )}
    </div>
  )
}

// ── Player aggregate ───────────────────────────────────────────────────────────

interface PlayerRow {
  user_id: string
  name: string
  avatar_url: string | null
  played: number
  /**
   * Matches this player could have been at — the range, cut at the moment they
   * turned up. Everyone used to share `ranged.length`, which made the rate a
   * fixed multiple of `played` and the "Rate" sort an exact copy of "Played".
   */
  called: number
  /** Where that window opens (unix s); 0 when it is the whole range. */
  since: number
  streak: number      // consecutive matches played, counting back from the last
}

/**
 * When a player's window opens.
 *
 * Two sources, and the earlier one wins. `clan_joined_at` is the date they were
 * given a clan role; their first sign-up in the range is proof they were here
 * by then. Taking the minimum is what keeps the number honest while the join
 * dates are young: the table only started watching recently and was seeded from
 * Discord's audit log, which keeps 45 days — so a member of two years can carry
 * a date from last week. Anyone with a footprint older than their recorded date
 * is dated by the footprint instead.
 *
 * It also holds the invariant the rate depends on: every match they played is
 * at or after the window opens, so `played` can never exceed `called`.
 */
function windowStart(joinedAt: number, firstSignUp: number): number {
  // No date on file — an ex-member, or someone the table never stamped. There
  // is no evidence they are new, so they answer for the whole range.
  if (joinedAt <= 0) return 0

  // A sign-up older than the recorded date proves the date is wrong rather
  // than proving they are new: `clan_joins` is young and was seeded from
  // Discord's audit log, which keeps 45 days, so a member of two years can
  // carry last week's date. We cannot tell when they actually joined, so they
  // answer for the whole range.
  //
  // Opening the window at that first sign-up instead — the obvious reading —
  // quietly excuses every match before it, which is precisely the stretch this
  // page exists to show. Measured on a 20-match range: someone absent for the
  // first fourteen scored 80% that way and 20% this way.
  if (firstSignUp > 0 && firstSignUp < joinedAt) return 0

  // The date is consistent with everything we have seen of them: trust it.
  return joinedAt
}

// ── Inactive members ───────────────────────────────────────────────────────────

interface InactiveRow {
  discord_id: string
  name: string
  avatar_url: string | null
  removed_roles: string[]
  inactivated_at: number
}

interface InactiveResponse {
  enabled: boolean
  threshold: number
  window: { id: string; title: string; start_time: number }[]
  members: InactiveRow[]
}

const TEAM_ROLE_LABEL: Record<string, 'teamMember' | 'teamAcademy'> = {
  '1545387929369714688': 'teamMember',
  '1545388634834866226': 'teamAcademy',
}

/**
 * Members the backend moved to the inactive role (members/inactivity.py): the
 * last N dashboard matches, and not one answer from them — not even "not sure"
 * or "not coming". The page only reads the list; the decision and the role
 * writes are the backend's periodic pass, so the rule lives in one place.
 * "Geri al" gives back exactly the roles that were taken.
 */
function InactiveBand({ onPickPlayer, selectedId, T, locale }: {
  onPickPlayer: (id: string | null) => void
  selectedId: string | null
  T: ReturnType<typeof useLanguage>['T']
  locale: string
}) {
  const [data, setData] = useState<InactiveResponse | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/activity/inactive', { credentials: 'include' })
      if (res.ok) setData(await res.json())
    } catch { /* the band just stays empty */ }
  }, [])
  useEffect(() => { load() }, [load])

  const restore = async (id: string) => {
    setBusy(id)
    setError(false)
    try {
      const res = await fetch(`/api/activity/inactive/${id}/restore`, {
        method: 'POST', credentials: 'include',
      })
      if (!res.ok) setError(true)
      await load()
    } finally {
      setBusy(null)
    }
  }

  if (!data) return null
  const fmt = (ts: number) =>
    new Date(ts * 1000).toLocaleDateString(locale, { day: 'numeric', month: 'short' })

  return (
    <Panel
      title={T.activity.inactiveTitle}
      hint={T.activity.inactiveHint.replace('{n}', String(data.threshold))}
      action={
        <span className="font-display text-sm font-bold tabular-nums text-primary">
          {data.members.length}
        </span>
      }
    >
      {!data.enabled && (
        <p className="mb-3 rounded-sm border border-white/10 bg-white/[0.035] px-3 py-2 text-xs text-muted-foreground">
          {T.activity.inactiveDisabled}
        </p>
      )}
      {error && (
        <p className="mb-3 rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {T.activity.inactiveRestoreFailed}
        </p>
      )}
      {data.members.length === 0 ? (
        <p className="rounded-sm border border-dashed border-white/10 py-6 text-center text-xs text-muted-foreground/70">
          {data.window.length < data.threshold
            ? T.activity.inactiveWaiting
                .replace('{have}', String(data.window.length))
                .replace('{n}', String(data.threshold))
            : T.activity.inactiveNone}
        </p>
      ) : (
        <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {data.members.map(p => {
            const isSel = p.discord_id === selectedId
            const teams = p.removed_roles
              .map(r => TEAM_ROLE_LABEL[r])
              .filter(Boolean)
              .map(k => T.activity[k])
              .join(', ')
            return (
              <div
                key={p.discord_id}
                className={`flex items-center gap-2.5 rounded-sm border px-2.5 py-2 transition-colors ${
                  isSel ? 'border-primary/50 bg-primary/10' : 'border-white/10 bg-white/[0.035]'
                }`}
              >
                <button
                  onClick={() => onPickPlayer(isSel ? null : p.discord_id)}
                  className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                >
                  <Avatar
                    src={p.avatar_url}
                    name={p.name}
                    className="h-7 w-7 shrink-0 rounded-full text-xs ring-1 ring-white/10"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium leading-tight">{p.name}</span>
                    <span className="mt-0.5 block truncate text-[10px] text-muted-foreground/70">
                      {T.activity.inactiveSince.replace('{date}', fmt(p.inactivated_at))}
                      {teams ? ` · ${teams}` : ''}
                    </span>
                  </span>
                </button>
                <button
                  onClick={() => restore(p.discord_id)}
                  disabled={busy === p.discord_id}
                  title={T.activity.inactiveRestoreHint}
                  className="flex shrink-0 items-center gap-1 rounded-sm border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-semibold text-foreground/80 transition-colors hover:bg-white/10 disabled:opacity-50"
                >
                  <Undo2 className="h-3 w-3" />
                  {T.activity.inactiveRestore}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </Panel>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function Activity() {
  const { user } = useAuth()
  const { T, lang } = useLanguage()
  const locale = lang === 'tr' ? 'tr-TR' : 'en-GB'
  const canView = isOperator(user)

  const [items, setItems] = useState<MatchItem[]>([])
  const [members, setMembers] = useState<{ id: string; name: string; avatar_url: string; joinedAt: number }[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  const [days, setDays] = useState<30 | 60 | 90>(90)
  const [matchId, setMatchId] = useState<string | null>(null)
  const [playerId, setPlayerId] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<'played' | 'rate'>('played')
  const [query, setQuery] = useState('')

  const load = useCallback(async (force = false) => {
    setLoading(true)
    setFailed(false)
    try {
      const [actRes, memRes] = await Promise.all([
        fetch(force ? '/api/activity?force=true' : '/api/activity', { credentials: 'include' }),
        fetch('/api/members', { credentials: 'include' }),
      ])
      if (!actRes.ok) throw new Error()
      const json: ActivityResponse = await actRes.json()
      setItems(json.items ?? [])
      if (memRes.ok) {
        const raw: {
          id: string; display_name: string; avatar_url: string; clan_joined_at?: number
        }[] = await memRes.json()
        setMembers(raw.map(m => ({
          id: m.id,
          name: m.display_name,
          avatar_url: m.avatar_url,
          joinedAt: m.clan_joined_at ?? 0,
        })))
      }
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (canView) load()
    else setLoading(false)
  }, [canView, load])

  /** Escape backs out one level: the match first, then the player. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (matchId) setMatchId(null)
      else if (playerId) setPlayerId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [matchId, playerId])

  // ── The range is the universe: everything below reads `ranged` ──────────────

  const ranged = useMemo(() => {
    const cutoff = Date.now() / 1000 - days * 24 * 3600
    return items
      .filter(m => (m.start_time || 0) >= cutoff)
      .sort((a, b) => (a.start_time || 0) - (b.start_time || 0))
  }, [items, days])

  // A match selected in one range may fall outside the next one.
  useEffect(() => {
    if (matchId && !ranged.some(m => m.id === matchId)) setMatchId(null)
  }, [ranged, matchId])

  const totals = useMemo(() => {
    const count = ranged.length
    const avg = count === 0 ? 0 : Math.round(ranged.reduce((s, m) => s + m.active_count, 0) / count)
    const best = count === 0 ? 0 : Math.max(...ranged.map(m => m.active_count))
    const pool = new Set<string>()
    ranged.forEach(m => m.sign_ups.forEach(s => { if (s.is_active) pool.add(s.user_id) }))
    return { count, avg, best, pool: pool.size }
  }, [ranged])

  /**
   * Everyone who played in the range, plus every current guild member — a
   * member with no sign-ups is the finding, not a gap, so they rank last
   * rather than vanish.
   */
  const players = useMemo<PlayerRow[]>(() => {
    const map = new Map<string, PlayerRow>()
    const firstSeen = new Map<string, number>()   // earliest sign-up in range
    const joinedAt = new Map(members.map(m => [m.id, m.joinedAt]))

    const ensure = (id: string, name: string, avatar: string | null) => {
      let row = map.get(id)
      if (!row) {
        row = { user_id: id, name, avatar_url: avatar, played: 0, called: 0, since: 0, streak: 0 }
        map.set(id, row)
      } else if (!row.avatar_url && avatar) {
        row.avatar_url = avatar
      }
      return row
    }

    // `ranged` is chronological, so the first sighting is the earliest one.
    for (const m of ranged) {
      for (const s of m.sign_ups) {
        const row = ensure(s.user_id, s.name, s.avatar_url)
        if (s.is_active) row.played++
        if (!firstSeen.has(s.user_id)) firstSeen.set(s.user_id, m.start_time || 0)
      }
    }
    for (const m of members) ensure(m.id, m.name, m.avatar_url)

    for (const row of map.values()) {
      const since = windowStart(joinedAt.get(row.user_id) ?? 0, firstSeen.get(row.user_id) ?? 0)
      row.since = since
      row.called = since === 0
        ? ranged.length
        : ranged.filter(m => (m.start_time || 0) >= since).length

      let streak = 0
      for (let i = ranged.length - 1; i >= 0; i--) {
        if (playerState(ranged[i], row.user_id) === 'played') streak++
        else break
      }
      row.streak = streak
    }
    return Array.from(map.values())
  }, [ranged, members])

  const ranked = useMemo(() => {
    const q = query.trim().toLowerCase()
    return players
      .filter(p => !q || p.name.toLowerCase().includes(q))
      .sort((a, b) => {
        if (sortBy === 'rate') {
          // Someone who joined after the last match has no rate to rank — they
          // go last rather than tying at a 0% they never had a chance to avoid.
          if ((a.called === 0) !== (b.called === 0)) return a.called === 0 ? 1 : -1
          const d = pct(b.played, b.called) - pct(a.played, a.called)
          if (d !== 0) return d
        }
        return b.played - a.played || a.name.localeCompare(b.name, locale)
      })
  }, [players, query, sortBy, locale])

  const selectedMatch  = matchId ? ranged.find(m => m.id === matchId) ?? null : null
  const selectedPlayer = playerId ? players.find(p => p.user_id === playerId) ?? null : null

  const playerHistory = useMemo(() => {
    if (!selectedPlayer) return []
    return [...ranged]
      .reverse()
      .map(m => ({ match: m, state: playerState(m, selectedPlayer.user_id) }))
  }, [ranged, selectedPlayer])

  // ── Locked ─────────────────────────────────────────────────────────────────

  if (!canView) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <Header title={T.activity.title} />
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="max-w-sm rounded-md border border-white/10 bg-black/25 p-8 text-center backdrop-blur-sm">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-sm border border-white/10 bg-white/[0.035]">
              <Lock className="h-5 w-5 text-muted-foreground/40" />
            </div>
            <h2 className="mt-4 flex items-center justify-center gap-2.5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
              <span aria-hidden className="h-px w-6 bg-primary" />
              {T.activity.comingSoon}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {T.activity.comingSoonDesc}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Header title={T.activity.title} />

      <div className="flex-1 space-y-5 overflow-y-auto p-4 md:p-6">
        {failed && (
          <p className="rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {T.activity.loadError}
          </p>
        )}

        {/* ── Turnout ─────────────────────────────────────────────────────── */}
        <Panel
          title={T.activity.turnout}
          hint={T.activity.turnoutHint}
          action={
            <>
              {([30, 60, 90] as const).map(d => (
                <Chip key={d} active={days === d} onClick={() => setDays(d)}>
                  {d}{T.activity.daysShort}
                </Chip>
              ))}
              <Button
                size="sm" variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => load(true)}
                disabled={loading}
                title={T.activity.refresh}
              >
                <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat value={String(totals.count)} label={T.activity.statMatches} />
            <Stat value={String(totals.avg)} label={T.activity.statAvg} tone="primary" />
            <Stat value={String(totals.best)} label={T.activity.statBest} />
            <Stat value={String(totals.pool)} label={T.activity.statPlayers} />
          </div>

          <div className="mt-5">
            {loading && ranged.length === 0 ? (
              <div className="h-[198px] animate-pulse rounded-sm bg-white/5" />
            ) : ranged.length === 0 ? (
              <p className="rounded-sm border border-dashed border-white/10 py-10 text-center text-xs text-muted-foreground/70">
                {T.activity.noMatches}
              </p>
            ) : (
              <TurnoutChart
                matches={ranged}
                selectedId={matchId}
                onSelect={setMatchId}
                playerId={selectedPlayer?.user_id ?? null}
                playerName={selectedPlayer?.name ?? null}
                T={T}
                locale={locale}
              />
            )}
          </div>

          {selectedMatch && (
            <MatchRoster
              match={selectedMatch}
              onClose={() => setMatchId(null)}
              onPickPlayer={setPlayerId}
              T={T}
              locale={locale}
            />
          )}
        </Panel>

        {/* ── Inactive members ─────────────────────────────────────────────── */}
        <InactiveBand onPickPlayer={setPlayerId} selectedId={playerId} T={T} locale={locale} />

        {/* ── Ranking + player ────────────────────────────────────────────── */}
        <div className="grid items-start gap-5 xl:grid-cols-[22rem_minmax(0,1fr)]">
          <Panel
            title={T.activity.ranking}
            action={
              <>
                <Chip active={sortBy === 'played'} onClick={() => setSortBy('played')}>
                  {T.activity.sortPlayed}
                </Chip>
                <Chip
                  active={sortBy === 'rate'}
                  onClick={() => setSortBy('rate')}
                  title={T.activity.sortRateHint}
                >
                  {T.activity.sortRate}
                </Chip>
              </>
            }
          >
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/50" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={T.activity.searchPlayers}
                className="w-full rounded-sm border border-white/10 bg-white/5 py-1.5 pl-8 pr-3 text-sm placeholder:text-muted-foreground/40 focus:border-primary focus:outline-none"
              />
            </div>

            {loading && ranked.length === 0 ? (
              <div className="mt-3 space-y-1.5">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-[46px] animate-pulse rounded-sm bg-white/5" />
                ))}
              </div>
            ) : ranked.length === 0 ? (
              <p className="mt-3 rounded-sm border border-dashed border-white/10 py-6 text-center text-xs text-muted-foreground/70">
                {T.activity.noPlayers}
              </p>
            ) : (
              <div className="mt-3 max-h-[28rem] space-y-1 overflow-y-auto pr-1 xl:max-h-[40rem]">
                {ranked.map((p, i) => {
                  const share  = pct(p.played, p.called)
                  const isMe   = p.user_id === user?.id
                  const isSel  = p.user_id === playerId
                  return (
                    <button
                      key={p.user_id}
                      onClick={() => setPlayerId(isSel ? null : p.user_id)}
                      className={`flex w-full items-center gap-2.5 rounded-sm border px-2.5 py-2 text-left transition-colors ${
                        isSel
                          ? 'border-primary/50 bg-primary/10'
                          : 'border-white/10 bg-white/[0.035] hover:border-white/20 hover:bg-white/[0.06]'
                      }`}
                    >
                      <span className={`w-4 shrink-0 text-right font-display text-[11px] font-bold tabular-nums ${
                        i === 0 ? 'text-primary' : 'text-muted-foreground/40'
                      }`}>
                        {i + 1}
                      </span>
                      <Avatar
                        src={p.avatar_url}
                        name={p.name}
                        className="h-7 w-7 shrink-0 rounded-full text-xs ring-1 ring-white/10"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline gap-1">
                          <span className="truncate text-xs font-medium leading-tight">{p.name}</span>
                          {isMe && <span className="shrink-0 text-[9px] text-primary/80">{T.activity.you}</span>}
                        </span>
                        <span className="mt-1.5 flex items-center gap-1.5">
                          <span className="h-1 flex-1 overflow-hidden rounded-full bg-white/8">
                            <span
                              className="block h-full rounded-full transition-all"
                              style={{ width: `${share}%`, background: INK.played }}
                            />
                          </span>
                          <span className="w-11 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground/60">
                            {p.played}/{p.called}
                          </span>
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </Panel>

          <Panel title={T.activity.player}>
            {!selectedPlayer ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <Users className="h-8 w-8 text-muted-foreground/20" />
                <p className="max-w-xs text-sm text-muted-foreground">{T.activity.selectPlayer}</p>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-3">
                  <Avatar
                    src={selectedPlayer.avatar_url}
                    name={selectedPlayer.name}
                    className="h-11 w-11 shrink-0 rounded-full text-base ring-2 ring-white/10"
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-display text-base font-bold leading-tight">
                      {selectedPlayer.name}
                    </h3>
                    {selectedPlayer.user_id === user?.id && (
                      <p className="mt-0.5 text-[11px] text-primary/80">{T.activity.you}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setPlayerId(null)}
                    aria-label={T.activity.close}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground/50 transition-colors hover:bg-white/8 hover:text-foreground xl:hidden"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <Stat
                    value={`${selectedPlayer.played}/${selectedPlayer.called}`}
                    label={T.activity.played}
                  />
                  <Stat
                    value={selectedPlayer.called === 0
                      ? '-'
                      : `${pct(selectedPlayer.played, selectedPlayer.called)}%`}
                    label={T.activity.rate}
                    tone="primary"
                    note={selectedPlayer.since
                      ? T.activity.sinceJoin.replace('{date}', new Date(selectedPlayer.since * 1000)
                          .toLocaleDateString(locale, { day: 'numeric', month: 'short' }))
                      : T.activity.wholeRange}
                  />
                  <Stat value={String(selectedPlayer.streak)} label={T.activity.streak} />
                </div>

                {ranged.length > 0 && (
                  <div>
                    <p className="flex items-center gap-2.5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
                      <span aria-hidden className="h-px w-6 shrink-0 bg-primary" />
                      {T.activity.record}
                    </p>
                    <p className="mb-2.5 mt-1.5 text-xs leading-relaxed text-muted-foreground">
                      {T.activity.recordHint}
                    </p>
                    <PlayerRecord
                      matches={ranged}
                      playerId={selectedPlayer.user_id}
                      selectedId={matchId}
                      onSelect={setMatchId}
                      T={T}
                      locale={locale}
                    />
                  </div>
                )}

                <div>
                  <p className="flex items-center gap-2.5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
                    <span aria-hidden className="h-px w-6 shrink-0 bg-primary" />
                    {T.activity.history}
                  </p>
                  {playerHistory.length === 0 ? (
                    <p className="mt-2.5 rounded-sm border border-dashed border-white/10 py-6 text-center text-xs text-muted-foreground/70">
                      {T.activity.noMatches}
                    </p>
                  ) : (
                    <div className="mt-2.5 max-h-[24rem] space-y-1 overflow-y-auto pr-1">
                      {playerHistory.map(({ match, state }) => {
                        const su = match.sign_ups.find(s => s.user_id === selectedPlayer.user_id)
                        const isSel = matchId === match.id
                        return (
                          <button
                            key={match.id}
                            onClick={() => setMatchId(isSel ? null : match.id)}
                            className={`flex w-full items-center gap-2.5 rounded-sm border px-2.5 py-2 text-left transition-colors ${
                              isSel
                                ? 'border-primary/50 bg-primary/10'
                                : 'border-white/10 bg-white/[0.035] hover:border-white/20 hover:bg-white/[0.06]'
                            }`}
                          >
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                              style={{
                                background: STATE_FILL[state],
                                border: state === 'absent' ? '1px solid rgba(255,255,255,0.2)' : undefined,
                              }}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-xs font-medium leading-tight">
                                {match.title}
                              </span>
                              <span className="mt-0.5 block text-[10px] text-muted-foreground">
                                {match.start_time
                                  ? new Date(match.start_time * 1000).toLocaleDateString(locale, {
                                      day: 'numeric', month: 'short', year: 'numeric',
                                    })
                                  : '-'}
                                {match.map_name ? ` · ${match.map_name}` : ''}
                              </span>
                            </span>
                            <span className="shrink-0 text-right">
                              <span className="block text-[10px] text-muted-foreground/70">
                                {stateLabel(state, T)}
                              </span>
                              {su?.class_name && (
                                <span className="mt-0.5 block text-[10px] text-foreground/60">
                                  {su.class_name}
                                </span>
                              )}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  )
}
